import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { CheckCircle, Award, Wallet, ChevronRight } from "lucide-react";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import insurancePolicyService from "../lib/InsurancePolicyService";
import { useAuth } from "../hooks/useAuth";

const RewardConfirmationScreen = ({ buyerData, contactData, onContinue }) => {
  const { trackButtonClick } = useInteractionTracking();
  const isSimplyIOA = buyerData?.name?.toLowerCase() === "simplyioa";
  const isInsurify = buyerData?.name?.toLowerCase()?.includes("insurify");
  const isNovo = buyerData?.name?.toLowerCase()?.includes("novo");
  const isAADirect = buyerData?.name?.toLowerCase() === "aadirect";
  const [shopNowLink, setShopNowLink] = useState(buyerData?.shopNowLink);
  const [loadingRedirect, setLoadingRedirect] = useState(isInsurify || isNovo);
  const AADIRECT_PHONE = "4048507169"; // ADD THIS LINE
  const AADIRECT_PHONE_DISPLAY = "(404) 850-7169"; // ADD THIS LINE
  const { getIdToken } = useAuth();

  useEffect(() => {
    analyticsService.trackPageView("you-are-all-set", {
      buyer: buyerData?.name,
    });
    if (isInsurify) {
      const fetchRedirect = async () => {
        try {
          const token = await getIdToken();
          const url = await insurancePolicyService.getInsurifyRedirectUrl(
            token
          );
          setShopNowLink(url);
        } catch (e) {
          console.error("Failed to fetch Insurify redirect URL", e);
        } finally {
          setLoadingRedirect(false);
        }
      };

      fetchRedirect();
    } else if (isNovo) {
      const fetchRedirect = async () => {
        try {
          const token = await getIdToken();
          const novoAdResponse = await insurancePolicyService.getNovoAdResponse(
            token
          );

          // Fire impression pixel if exists
          if (novoAdResponse?.Ads?.[0]?.ImpressionPixel) {
            const impressionSrc = novoAdResponse.Ads[0].ImpressionPixel.match(
              /src="([^"]+)"/
            )[1].replace("{position}", "1");

            const impressionPixel = new Image();
            impressionPixel.src = impressionSrc;
          }
         
          // Set shop now link to the click URL
          if (novoAdResponse?.Ads?.[0]?.ClickUrl) {
            setShopNowLink(novoAdResponse.Ads[0].ClickUrl);
          }
        } catch (e) {
          console.error("Failed to fetch Novo ad response", e);
        } finally {
          setLoadingRedirect(false);
        }
      };

      fetchRedirect();
    }
  }, []);

  const startShopping = () => {
    if (!shopNowLink) return;
    window.open(shopNowLink, "_blank");
  };

  const openDialer = () => {
    trackButtonClick("call-aadirect", {
      step_name: "you-are-all-set",
      buyer: buyerData?.name,
    });
    window.location.href = `tel:${AADIRECT_PHONE}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with celebration */}
      <div className="text-center space-y-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl uppercase font-bold">You're All Set</h1>
        {/* Conditional content based on buyer */}
        {isSimplyIOA ? (
          <>
            <p className="text-darkGrey">
              We've saved your contact preferences and passed them to{" "}
              <span style={{ color: "#7A5CFA" }}>SimplyIOA</span>. They have
              your Ownli Insurance Profile, are getting to work, and will be in
              touch within two business days.
            </p>
            <p className="text-darkGrey">
              Your reward will be added to your Ownli Wallet once your profile
              is fully verified.
            </p>
          </>
        ) : isAADirect ? (
          // ADD THIS NEW SECTION:
          <>
            <p className="text-darkGrey">
              Great! Your offer has been accepted and your reward is pending.
            </p>
            <p className="text-darkGrey">
              <strong>Next step:</strong> Call{" "}
              <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span> at{" "}
              <span style={{ color: "#7A5CFA", fontWeight: "bold" }}>
                {AADIRECT_PHONE_DISPLAY}
              </span>{" "}
              to complete your quote and activate your reward.
            </p>
          </>
        ) : (
          <>
            <p className="text-darkGrey">
              We've saved your contact preferences and passed them to{" "}
              <span style={{ color: "#7A5CFA" }}>
                {buyerData?.name || "our partner"}
              </span>
              . Your reward will be added to your Ownli Wallet once your profile
              is fully verified.
            </p>
            {/* <p className="text-darkGrey">
              Now, let's set up your Wallet for seamless rewards.
            </p> */}
          </>
        )}
      </div>

      {/* Wallet Setup Card */}
      {/* <Card className="bg-blue-50 border-blue-100">
        <CardContent className="py-6">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-6 h-6 text-blue-600" />
              <span className="text-blue-700 font-medium">Reward Pending</span>
            </div>
            <p className="text-sm text-blue-600 text-center mt-2">
              Your reward will be processed as soon as your profile is verified
            </p>
          </div>
        </CardContent>
      </Card> */}

      {/* Contact Method Confirmation */}
      {/* <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-3">Contact Preferences Saved</h3>
          
          <div className="space-y-2">
            {contactData?.methods?.includes('email') && contactData?.email && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{contactData.email}</span>
              </div>
            )}
            
            {contactData?.methods?.includes('phone') && contactData?.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{contactData.phone}</span>
              </div>
            )}
            
            {contactData?.methods?.includes('sms') && contactData?.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">SMS:</span>
                <span className="font-medium">{contactData.phone}</span>
              </div>
            )}
            
            {(!contactData?.methods || contactData.methods.length === 0) && (
              <div className="text-sm text-gray-600">
                Your contact preferences have been saved.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
       */}
      {/* Shop now Button */}
      <button
        disabled={loadingRedirect}
        onClick={() => {
          trackButtonClick("start-shopping", {
            step_name: "you-are-all-set",
            buyer: buyerData?.name,
          });
          isAADirect ? openDialer() : startShopping();
        }}
        className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-[10px] transition-colors ${
          loadingRedirect
            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
            : "bg-purpleGradient text-white hover:bg-purpleGradientDark"
        }`}
      >
        {isSimplyIOA
          ? "Learn More"
          : isAADirect
          ? `Call ${AADIRECT_PHONE_DISPLAY}`
          : loadingRedirect
          ? "Preparing your link..."
          : "Start Shopping Now"}

        {/* <ChevronRight className="w-4 h-4" /> */}
      </button>
      {/* Continue Button */}
      {/* <button
        onClick={() => {
          trackButtonClick("go-to-wallet", {
            step_name: "you-are-all-set",
          });
          onContinue();
        }}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
      >
        Set Up Wallet
      </button> */}
    </div>
  );
};

export default RewardConfirmationScreen;
